/**
 * PostHog Debug Utilities
 * Helper functions to debug and monitor PostHog events
 */

export const logPostHogEvent = (eventName, properties = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.group(`🧪 PostHog Event: ${eventName}`);
    console.log('Properties:', properties);
    console.log('Timestamp:', new Date().toISOString());
    console.groupEnd();
  }
};

export const testBlogViewTracking = (posthog) => {
  if (!posthog) {
    console.warn('PostHog not initialized');
    return;
  }

  console.group('🧪 Testing Blog View Tracking (New ID-based System)');
  
  // Test new blog read event with blog ID
  const testBlogId = 99;
  const testBlogSlug = 'test-blog-tracking';
  const testBlogTitle = 'Test Blog Tracking System';
  
  posthog.capture('blog_read', {
    blog_id: testBlogId,
    blog_title: testBlogTitle,
    blog_slug: testBlogSlug,
    timestamp: new Date().toISOString(),
    session_id: posthog.get_session_id() || 'unknown',
    test_event: true
  });
  
  console.log('✅ Test blog_read event sent with blog ID');
  console.log('Event data:', {
    blog_id: testBlogId,
    blog_title: testBlogTitle,
    blog_slug: testBlogSlug,
    session_id: posthog.get_session_id()
  });
  
  // Test engagement event with blog ID
  posthog.capture('blog_engagement', {
    blog_id: testBlogId,
    blog_slug: testBlogSlug,
    engagement_type: 'reading_time',
    reading_time_seconds: 30,
    blog_title: testBlogTitle,
    timestamp: new Date().toISOString(),
    test_event: true
  });
  
  console.log('✅ Test blog_engagement event sent with blog ID');
  
  // Test session deduplication
  console.log('🔄 Testing session deduplication...');
  const sessionKey = `blog_read_${testBlogId}_${posthog.get_session_id() || 'unknown'}`;
  if (window.sessionStorage.getItem(sessionKey)) {
    console.log('✅ Session deduplication working - duplicate would be blocked');
  } else {
    console.log('ℹ️ First test in session - would create sessionStorage entry');
  }
  
  console.groupEnd();
};

export const monitorPostHogEvents = (posthog) => {
  if (!posthog || process.env.NODE_ENV !== 'development') {
    return;
  }

  // Log all events being captured
  const originalCapture = posthog.capture;
  posthog.capture = function(eventName, properties) {
    logPostHogEvent(eventName, properties);
    return originalCapture.call(this, eventName, properties);
  };

  console.log('🔍 PostHog event monitoring enabled');
};

export const checkPostHogConfiguration = (posthog) => {
  console.group('🔧 PostHog Configuration Check');
  
  if (!posthog) {
    console.error('❌ PostHog not initialized');
    console.groupEnd();
    return false;
  }
  
  console.log('✅ PostHog initialized');
  console.log('Config:', {
    api_host: posthog.config?.api_host,
    project_api_key: posthog.config?.token ? '***' + posthog.config.token.slice(-4) : 'missing',
    session_id: posthog.get_session_id(),
    distinct_id: posthog.get_distinct_id()
  });
  
  // Check if events are being queued
  const queueLength = posthog._requestQueue?.length || 0;
  if (queueLength > 0) {
    console.warn(`⚠️ ${queueLength} events in queue (waiting to be sent)`);
  } else {
    console.log('✅ Event queue is empty (events being sent immediately)');
  }
  
  console.groupEnd();
  return true;
};

// Make these available globally in development
if (process.env.NODE_ENV === 'development') {
  window.posthogDebug = {
    logEvent: logPostHogEvent,
    testBlogTracking: testBlogViewTracking,
    monitorEvents: monitorPostHogEvents,
    checkConfig: checkPostHogConfiguration
  };
}